import { useState, useCallback, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";

import useFinanceConexoes from "@/hooks/useFinanceConexoes";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

import ConexoesHero from "./conexoes/ConexoesHero";
import BancosList from "./conexoes/BancosList";
import ComoFuncionaSection from "./conexoes/ComoFuncionaSection";
import ReconciliationModal from "./conexoes/ReconciliationModal";

// FIN-7 — ConexoesTab orquestrador.
//
// Lê estado via useFinanceConexoes (que respeita feature flag do
// backend). Em modo dormente, lista vazia + CTAs disabled. Em modo
// ativo, fluxo completo:
//   1. user clica "+" → initiate() → connect_token
//   2. abre Pluggy Connect modal (lazy import do SDK em services/pluggyConnect)
//   3. onSuccess do SDK → finalize(payload) persiste a Connection
//   4. polling automático até item Pluggy estabilizar (status updated)
//
// Disconnect: confirm dialog antes de DELETE — irreversível com side
// effect na cobrança Pluggy.

const ROW_TRANSITION = { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] };

export default function ConexoesTab(/* { onTalk } */) {
  const c = useFinanceConexoes();
  const { toast } = useToast();
  const [pendingDisconnect, setPendingDisconnect] = useState(null);
  const [isOpening, setIsOpening] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileConnectionId, setReconcileConnectionId] = useState(null);

  // Carrega candidates de cada Connection ativa quando lista chega
  useEffect(() => {
    if (!c.enabled || c.items.length === 0) return;
    // Pega da primeira Connection (em geral é só uma; se houver várias,
    // o user vê banner agregado e pode abrir cada uma — escopo enxuto).
    const first = c.items[0];
    if (first?.id) {
      c.fetchCandidates(first.id);
      setReconcileConnectionId(first.id);
    }
  }, [c.enabled, c.items, c.fetchCandidates]);

  const candidatesCount = c.candidates?.length ?? 0;

  const handleAddBank = useCallback(async () => {
    if (!c.enabled || isOpening) return;
    setIsOpening(true);
    try {
      const { connect_token } = await c.initiate();
      // Lazy import do SDK só agora — não baixa bundle em modo dormente.
      const { openConnectModal } = await import("@/services/pluggyConnect");
      await openConnectModal({
        connectToken: connect_token,
        onSuccess: async (data) => {
          try {
            const item = data?.item || {};
            await c.finalize({
              pluggy_item_id: item.id || item.itemId || "",
              institution_name: item.connector?.name || item.institution || "Banco",
              institution_logo_url: item.connector?.imageUrl || null,
              institution_id: item.connector?.id ? String(item.connector.id) : null,
            });
            toast({
              title: "Banco conectado",
              description: "Sincronização iniciada. Pode levar alguns minutos.",
            });
          } catch (err) {
            toast({
              title: "Erro ao salvar conexão",
              description: String(err?.message || err),
              variant: "destructive",
            });
          }
        },
        onError: (err) => {
          toast({
            title: "Pluggy retornou erro",
            description: String(err?.message || err || "unknown"),
            variant: "destructive",
          });
        },
      });
    } catch (err) {
      toast({
        title: "Não foi possível abrir o conector",
        description: String(err?.message || err),
        variant: "destructive",
      });
    } finally {
      setIsOpening(false);
    }
  }, [c, isOpening, toast]);

  const handleRefresh = useCallback(async (connectionId) => {
    try {
      await c.refreshConnection(connectionId);
      toast({ title: "Atualizando", description: "Buscando dados mais recentes." });
    } catch (err) {
      toast({
        title: "Falha ao atualizar",
        description: String(err?.message || err),
        variant: "destructive",
      });
    }
  }, [c, toast]);

  const handleConfirmDisconnect = useCallback(async () => {
    if (!pendingDisconnect) return;
    const conn = pendingDisconnect;
    setPendingDisconnect(null);
    try {
      await c.disconnectConnection(conn.id);
      toast({
        title: "Banco desconectado",
        description: `${conn.institution_name} foi removido.`,
      });
    } catch (err) {
      toast({
        title: "Falha ao desconectar",
        description: String(err?.message || err),
        variant: "destructive",
      });
    }
  }, [pendingDisconnect, c, toast]);

  const handleResolveCandidate = useCallback(async (candidateId, decision) => {
    if (!reconcileConnectionId) return;
    try {
      await c.resolveCandidate(reconcileConnectionId, candidateId, decision);
      toast({
        title: decision === "confirm_duplicate" ? "Duplicata removida" : "Mantidas as duas",
      });
    } catch (err) {
      toast({
        title: "Falha ao salvar decisão",
        description: String(err?.message || err),
        variant: "destructive",
      });
    }
  }, [c, reconcileConnectionId, toast]);

  return (
    <div className="space-y-5 md:space-y-6" data-testid="finance-conexoes">
      {/* Hero permanente */}
      <ConexoesHero />

      {/* Banner de candidates pendentes (H0-S3) */}
      {c.enabled && candidatesCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-xl border border-status-warning/40 bg-status-warning/10 px-4 py-3 flex items-center justify-between gap-3"
          data-testid="conexoes-reconcile-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <AlertTriangle className="w-5 h-5 text-status-warning shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <div className="text-body font-semibold text-foreground">
                {candidatesCount}{" "}
                {candidatesCount === 1 ? "possível duplicata" : "possíveis duplicatas"} pra revisar
              </div>
              <div className="text-caption text-foreground-muted">
                Transações importadas do banco que parecem coincidir com lançamentos manuais.
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setReconcileOpen(true)}
            className="shrink-0 border-status-warning/40 text-status-warning hover:bg-status-warning/15"
            data-testid="conexoes-reconcile-open"
          >
            Revisar
          </Button>
        </motion.div>
      )}

      {/* Lista de bancos (vazia em modo dormente) */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.06 }}
      >
        <BancosList
          items={c.items}
          stats={c.stats}
          enabled={c.enabled}
          onAddBank={handleAddBank}
          onRefresh={handleRefresh}
          onDisconnect={(conn) => setPendingDisconnect(conn)}
        />
      </motion.section>

      {/* Como funciona — educativo, sempre visível */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.12 }}
      >
        <ComoFuncionaSection />
      </motion.section>

      {/* Erro global do hook (só em modo ativo) */}
      {c.error && c.enabled && (
        <div
          className="text-caption text-status-error px-2"
          data-testid="conexoes-error"
        >
          {c.error}
        </div>
      )}

      {/* Reconciliation modal (H0-S3) */}
      <ReconciliationModal
        open={reconcileOpen}
        onOpenChange={setReconcileOpen}
        candidates={c.candidates || []}
        onResolve={handleResolveCandidate}
      />

      {/* Confirm dialog de desconexão */}
      <AlertDialog
        open={Boolean(pendingDisconnect)}
        onOpenChange={(o) => { if (!o) setPendingDisconnect(null); }}
      >
        <AlertDialogContent data-testid="conexoes-disconnect-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Desconectar {pendingDisconnect?.institution_name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              A conexão será revogada na Pluggy e o banco para de sincronizar.
              Suas transações já importadas continuam disponíveis.
              Você pode reconectar a qualquer momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDisconnect}
              className="bg-status-error text-foreground hover:bg-status-error/90"
            >
              Desconectar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
